import { useAuth0 } from "@auth0/auth0-react";
import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import HelpIcon from "@mui/icons-material/Help";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonIcon from "@mui/icons-material/Person";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  Box,
  Collapse,
  Drawer,
  IconButton,
  Link,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useApi } from "../hooks/useApi";
import { ClientProfile } from "../types/client";
import { Profile } from "../types/contact";

interface Props {}

const MobileMenu: FunctionComponent<Props> = (props: Props): JSX.Element => {
  const { data: profile } = useApi<Profile>("profile");
  const { logout } = useAuth0();
  const [isPrimaryAdmin, setIsPrimaryAdmin] = useState<boolean>(false);
  const [openTimesheets, setOpenTimesheets] = useState(true);
  const [openSettings, setOpenSettings] = useState(false);
  const [open, setOpen] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const handleClickTimesheets = () => {
    setOpenTimesheets(!openTimesheets);
  };

  const handleClickSettings = () => {
    setOpenSettings(!openSettings);
  };

  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // useEffect(() => {
  //   const clients: ClientProfile[] = profile?.results?.clients || [];
  //   const isAdmin = clients.some((client) => client.isPrimary);
  //   setShowAdmin(isAdmin);
  // }, [profile]);

  useEffect(() => {
    if (profile?.results?.clients && profile.results.clients.length > 0) {
      const clients: ClientProfile[] = profile?.results?.clients || [];
      setIsPrimaryAdmin(clients.some((client) => client.isPrimary));
    }
  }, [profile]);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const MenuList = (
    <Box sx={{ width: 270 }} role="presentation">
      <List>
        <ListItemButton onClick={handleClickTimesheets}>
          <ListItemIcon>
            <AccessTimeFilledIcon />
          </ListItemIcon>
          <ListItemText primary={t("timesheets.title")}></ListItemText>
          {openTimesheets ? (
            <ExpandLess sx={{ color: "rgba(0, 0, 0, 0.5)" }} />
          ) : (
            <ExpandMore sx={{ color: "rgba(0, 0, 0, 0.5)" }} />
          )}
        </ListItemButton>

        <Collapse in={openTimesheets} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton
              sx={{ pl: 4, pt: "2px", pb: "2px" }}
              onClick={() => {
                navigate("/");
                handleClose();
              }}
              selected={location.pathname === "/"}
            >
              <ListItemIcon></ListItemIcon>
              <ListItemText secondary={t("current.outstanding2")} />
            </ListItemButton>

            {isSmallScreen && (
              <ListItemButton
                sx={{ pl: 4, pt: "2px", pb: "2px" }}
                onClick={() => {
                  navigate("/recentlyapproved");
                  handleClose();
                }}
                selected={location.pathname === "/recentlyapproved"}
              >
                <ListItemIcon></ListItemIcon>
                <ListItemText secondary={t("current.recentlyApproved")} />
              </ListItemButton>
            )}

            <ListItemButton
              sx={{ pl: 4, pt: "2px", pb: "2px" }}
              onClick={() => {
                navigate("/history");
                handleClose();
              }}
              selected={location.pathname === "/history"}
            >
              <ListItemIcon></ListItemIcon>
              <ListItemText secondary={t("history.title")} />
            </ListItemButton>
          </List>
        </Collapse>

        <ListItemButton
          selected={location.pathname === "/contacts"}
          onClick={() => {
            navigate("/contacts");
            handleClose();
          }}
        >
          <ListItemIcon>
            <PersonIcon />
          </ListItemIcon>
          <ListItemText
            primary={t("contacts.title")}
            sx={{ marginRight: "20px" }}
          ></ListItemText>
        </ListItemButton>

        <ListItemButton onClick={handleClickSettings}>
          <ListItemIcon>
            <SettingsIcon />
          </ListItemIcon>
          <ListItemText primary={t("settings.title")}></ListItemText>
          {openSettings ? (
            <ExpandLess sx={{ color: "rgba(0, 0, 0, 0.5)" }} />
          ) : (
            <ExpandMore sx={{ color: "rgba(0, 0, 0, 0.5)" }} />
          )}
        </ListItemButton>

        <Collapse in={openSettings} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton
              sx={{ pl: 4, pt: "2px", pb: "2px" }}
              onClick={() => {
                navigate("/settings");
                handleClose();
              }}
              selected={
                (location.pathname.split("?")[0] === "/settings" &&
                  location.search === "") ||
                (location.pathname.split("?")[0] === "/settings" &&
                  location.search === "?tab=1")
              }
            >
              <ListItemIcon></ListItemIcon>
              <ListItemText secondary={t("profilePage.title")} />
            </ListItemButton>

            {isPrimaryAdmin && (
              <>
                <ListItemButton
                  sx={{ pl: 4, pt: "2px", pb: "2px" }}
                  onClick={() => {
                    navigate("/settings?tab=2");
                    handleClose();
                  }}
                  selected={
                    location.pathname.split("?")[0] === "/settings" &&
                    location.search === "?tab=2"
                  }
                >
                  <ListItemIcon></ListItemIcon>
                  <ListItemText secondary={t("clientPage.title")} />
                </ListItemButton>

                <ListItemButton
                  sx={{ pl: 4, pt: "2px", pb: "2px" }}
                  onClick={() => {
                    navigate("/settings?tab=3");
                    handleClose();
                  }}
                  selected={
                    location.pathname.split("?")[0] === "/settings" &&
                    location.search === "?tab=3"
                  }
                >
                  <ListItemIcon></ListItemIcon>
                  <ListItemText secondary={t("adminPage.title")} />
                </ListItemButton>
              </>
            )}
          </List>
        </Collapse>

        <ListItemButton
          selected={location.pathname === "/faq"}
          onClick={() => {
            navigate("/faq");
            handleClose();
          }}
        >
          <ListItemIcon>
            <HelpIcon />
          </ListItemIcon>
          <ListItemText
            primary={t("FAQ.title")}
            sx={{ marginRight: "20px" }}
          ></ListItemText>
        </ListItemButton>

        <ListItemButton>
          <ListItemIcon>
            <OpenInNewIcon />
          </ListItemIcon>
          <Link
            target="_blank"
            href="https://storage.googleapis.com/mi_bucket_ta/PDF/FileStore/TOB_TEMP.pdf"
            sx={{ marginRight: "20px" }}
            underline="none"
            color={"black"}
          >
            {t("termsOfBusiness.title")}
          </Link>
        </ListItemButton>

        {isSmallScreen && (
          <ListItemButton
            onClick={() =>
              logout({
                logoutParams: {
                  returnTo: window.location.origin + "/login",
                },
              })
            }
          >
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary={t("logout.title")}></ListItemText>
          </ListItemButton>
        )}
      </List>
    </Box>
  );

  return (
    <>
      <IconButton aria-label="menu" onClick={handleClick}>
        <MenuIcon />
      </IconButton>

      <Drawer
        open={open}
        onClose={handleClose}
        sx={{ display: "flex", justifyContent: "center" }}
      >
        <Stack sx={{ mt: "15px" }}>{MenuList}</Stack>
      </Drawer>
    </>
  );
};

export default MobileMenu;
